/**
 * Copyright (c) 2026, CheckPlus Limited
 */

/**
 * The props that make a plain element behave like the control it is announced
 * as: focusable, and activated by Enter or Space as well as by a click.
 *
 * The marketplace draws its buttons as styled `div`s and `span`s, and a good
 * deal of it carries `role="button"` without either of those — which tells a
 * screen reader it is a button and then refuses the two keys a button
 * responds to. Nonprofit news made a whole feed row one of those, so the only
 * way to open a post was a mouse.
 *
 * Spread rather than wrapped in a component, so it can be dropped onto the
 * elements the artboard already specifies without changing any of them into
 * something else:
 *
 *   <div className="…" {...activate(row.open)}>
 *
 * A null or undefined handler yields no props at all, which is how an inert
 * element — a nonprofit the directory no longer lists — stays genuinely
 * inert rather than becoming a focus stop that does nothing.
 *
 * Space is prevented from scrolling the page, which is what it would
 * otherwise do on a focused non-button.
 *
 * @param {Function} [onActivate] What a click or Enter/Space should do
 * @param {Object} [options]
 * @param {String} [options.role] Defaults to "button"
 * @param {Boolean} [options.stopPropagation] For a control nested inside
 *   another one — the report link inside a post card, say — so activating it
 *   does not also activate what it sits in
 * @return {Object} Props to spread, or an empty object
 */
export const activate = (onActivate, options) => {
  if (typeof onActivate !== "function") {
    return {};
  }
  const opts = options || {};
  const fire = (e) => {
    if (opts.stopPropagation && e && e.stopPropagation) {
      e.stopPropagation();
    }
    onActivate(e);
  };
  return {
    role: opts.role || "button",
    tabIndex: 0,
    onClick: fire,
    onKeyDown: (e) => {
      if (e.key !== "Enter" && e.key !== " " && e.key !== "Spacebar") {
        return;
      }
      // Or Space scrolls the page out from under whatever was just opened.
      e.preventDefault();
      fire(e);
    },
  };
};

export default activate;
